import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";
import type { ConnectionState, ConsoleClient } from "./client";
import type { SessionStore } from "./session";

export type ConsoleApi = { client: ConsoleClient; session: SessionStore };

const ApiContext = createContext<ConsoleApi | null>(null);

export function ApiProvider({ api, children }: { api: ConsoleApi; children: ReactNode }) {
  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

export function useApi(): ConsoleApi {
  const api = useContext(ApiContext);
  if (!api) throw new Error("useApi must be used inside <ApiProvider>");
  return api;
}

/**
 * The API when there is one, null when there is not — for a component that
 * does something *extra* with the server and must still render without it.
 *
 * Only the header's language and theme pickers use this. They save the choice
 * to the staff member's account, which is worth doing and not worth requiring:
 * they are rendered by PageTop, which 17 screens mount, and several of those
 * are tested in isolation with no provider around them. Threading a client
 * through every one of those call sites to reach two `select` elements would be
 * the wrong shape.
 *
 * Do not reach for this to avoid wiring a provider. Everything that *needs* the
 * server uses `useApi` and fails loudly, which is the right behaviour for
 * anything whose job is talking to it.
 */
export function useOptionalApi(): ConsoleApi | null {
  return useContext(ApiContext);
}

export function useSessionToken(): string | null {
  const { session } = useApi();
  return useSyncExternalStore(session.subscribe, session.get);
}

export function useConnection(): ConnectionState {
  const { client } = useApi();
  return useSyncExternalStore(client.connection.subscribe, client.connection.get);
}
